import React, {useCallback, useEffect, useRef, useState} from "react";
import {processHtmlForUrls} from "../NotesTab/utils/htmlUtils";
import "./NotificationComponent.css";

const NotificationComponent = ({ userId, onClose, onNotificationCountUpdate, onNavigateToNote }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const observerRef = useRef();
  const lastNotificationRef = useRef();

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // Filter notifications based on showOnlyUnread
  const filteredNotifications = showOnlyUnread 
    ? notifications.filter(notification => !notification.isRead)
    : notifications;

  // Process notification message to render HTML properly
  const processNotificationMessage = (message) => {
    if (!message) return "";
    
    // First process URLs in the HTML
    const withUrls = processHtmlForUrls(message);
    
    // Then highlight if needed (you can pass a highlight term if you have search functionality)
    // For now, just return the processed HTML with URLs
    return withUrls;
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async (page = 1, append = false) => {
    if (loading) return;
    setLoading(true);
    
    try {
      const response = await fetch(
        `${apiUrl}/Notification/GetNotifications?userId=${userId}&pageNumber=${page}&pageSize=20`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          if (append) {
            setNotifications(prev => [...prev, ...data.items]);
          } else {
            setNotifications(data.items);
          }
          setTotalPages(data.totalPages);
          setHasMore(data.hasNext);
          setPageNumber(page);
        }
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, userId, loading]);

  // Load more notifications
  const loadMore = useCallback(() => {
    if (hasMore && !loading && pageNumber < totalPages) {
      fetchNotifications(pageNumber + 1, true);
    }
  }, [hasMore, loading, pageNumber, totalPages, fetchNotifications]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (lastNotificationRef.current) {
      observer.observe(lastNotificationRef.current);
    }

    return () => {
      if (lastNotificationRef.current) {
        observer.unobserve(lastNotificationRef.current);
      }
    };
  }, [loading, hasMore, loadMore]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    setMarkingAsRead(notificationId);
    try {
      const response = await fetch(
        `${apiUrl}/Notification/MarkAsRead/${notificationId}?userId=${userId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(notif =>
            notif.id === notificationId
              ? { ...notif, isRead: true, readAt: new Date().toISOString() }
              : notif
          )
        );
        
        if (onNotificationCountUpdate) {
          const updatedUnreadCount = notifications.filter((notif) => !notif.isRead && notif.id !== notificationId).length;
          onNotificationCountUpdate(updatedUnreadCount);
        }
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    } finally {
      setMarkingAsRead(null);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (markingAllAsRead) return;
    
    setMarkingAllAsRead(true);
    try {
      const response = await fetch(
        `${apiUrl}/Notification/MarkAllAsRead?userId=${userId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=utf-8",
          },
        }
      );

      if (response.ok) {
        // Update local state - mark all as read
        setNotifications(prev =>
          prev.map(notif => ({
            ...notif,
            isRead: true,
            readAt: new Date().toISOString()
          }))
        );
        
        if (onNotificationCountUpdate) {
          onNotificationCountUpdate(0);
        }
        
        console.log("All notifications marked as read");
      } else {
        console.error("Failed to mark all as read:", response.status);
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (userId) {
      fetchNotifications(1, false);
    }
  }, [userId]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "NewSiteNote":
        return <i className="fas fa-clipboard-list notification-icon-new" />;
      case "NewTask":
        return <i className="fas fa-tasks notification-icon-task" />;
      case "Mention":
        return <i className="fas fa-at notification-icon-mention" />;
      default:
        return <i className="fas fa-bell notification-icon-default" />;
    }
  };

  const handleNotificationClick = (notification) => {
    console.log("🔔 Notification clicked:", notification);
    console.log("relatedSiteNoteId:", notification.relatedSiteNoteId);
    console.log("onNavigateToNote exists:", !!onNavigateToNote);
    console.log("onNavigateToNote value:", onNavigateToNote);
    
    if (notification.relatedSiteNoteId && onNavigateToNote) {
      console.log("🚀 Calling onNavigateToNote with:", notification.relatedSiteNoteId);
      onNavigateToNote(notification.relatedSiteNoteId);
    } else {
      console.warn("⚠️ Cannot navigate - missing:", {
        hasNoteId: !!notification.relatedSiteNoteId,
        hasCallback: !!onNavigateToNote
      });
    }
    
    if (onClose) onClose();
  };

  // Get unread count for badge (to disable button when no unread)
  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="notifications-dropdown-container">
      <div className="notifications-header">
        <h3>Notifications</h3>
        <div className="header-actions">
          {/* Show Only Unread Toggle */}
          <button
            className={`filter-toggle-btn ${showOnlyUnread ? 'active' : ''}`}
            onClick={() => setShowOnlyUnread(!showOnlyUnread)}
            title={showOnlyUnread ? "Show all notifications" : "Show only unread"}
          >
            {showOnlyUnread ? (
              <>
                <i className="fas fa-envelope-open" />
                <span>All</span>
              </>
            ) : (
              <>
                <i className="fas fa-envelope" />
                <span>Unread</span>
              </>
            )}
          </button>
          
          {/* Mark All as Read button */}
          <button
            className="mark-all-read-btn"
            onClick={markAllAsRead}
            disabled={markingAllAsRead || unreadCount === 0}
            title="Mark all as read"
          >
            {markingAllAsRead ? (
              <i className="fas fa-spinner fa-spin" />
            ) : (
              <i className="fas fa-check-double" />
            )}
          </button>
        </div>
      </div>
      
      <div className="notifications-list">
        {filteredNotifications.length === 0 && !loading ? (
          <div className="no-notifications">
            <i className="fas fa-bell-slash" />
            <p>
              {showOnlyUnread 
                ? "No unread notifications" 
                : "No notifications yet"}
            </p>
          </div>
        ) : (
          <>
            {filteredNotifications.map((notification, index) => (
              <div
                key={notification.id}
                ref={index === filteredNotifications.length - 1 ? lastNotificationRef : null}
                className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                onClick={() => {
                  console.log("🔴 TEST CLICK - Raw click on notification");
                  handleNotificationClick(notification);
                }}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className={`notification-title ${!notification.isRead ? 'unread-title' : ''}`}>
                    {notification.title}
                    {!notification.isRead && <span className="unread-dot"></span>}
                  </div>
                  {/* Updated message rendering with HTML processing */}
                  <div 
                    className="notification-message"
                    dangerouslySetInnerHTML={{ 
                      __html: processNotificationMessage(notification.message) 
                    }}
                  />
                  <div className="notification-time">
                    {formatDate(notification.createdAt)}
                  </div>
                </div>
                {!notification.isRead && (
                  <button
                    className="mark-read-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      markAsRead(notification.id);
                    }}
                    disabled={markingAsRead === notification.id}
                    title="Mark as read"
                  >
                    {markingAsRead === notification.id ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-check" />
                    )}
                  </button>
                )}
              </div>
            ))}
            {loading && (
              <div className="loading-more">
                <i className="fas fa-spinner fa-spin" />
                <span>Loading...</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationComponent;